import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MapPin, Clock, Tag, User, Save, PhoneCall, Send, Eye, Handshake, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { CrmShell as AdminShell } from "@/pages/crm/CrmShell";
import { statusBadge, priorityBadge } from "./AdminShell";

const STATUSES = ["New", "Contact attempted", "Contacted", "Qualified", "Viewing required", "Offer made", "Negotiating", "Under offer", "Completed", "Not suitable", "Nurture", "Closed"];
const PRIORITIES = ["Hot", "Warm", "Cold"];
const isoLocal = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 16); };

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [images, setImages] = useState({});
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/leads/${id}`); setLead(data); }
    catch (e) { toast.error("Lead not found"); navigate("/crm/leads"); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!lead?.attachments?.length) return;
    let revoked = [];
    (async () => {
      const map = {};
      for (const fid of lead.attachments) {
        try {
          const res = await api.get(`/files/${fid}`, { responseType: "blob" });
          const url = URL.createObjectURL(res.data); map[fid] = url; revoked.push(url);
        } catch (e) {}
      }
      setImages(map);
    })();
    return () => revoked.forEach((u) => URL.revokeObjectURL(u));
  }, [lead?.attachments]);

  const patch = async (updates) => {
    try { const { data } = await api.patch(`/leads/${id}`, updates); setLead((l) => ({ ...data, activity_history: l.activity_history })); load(); toast.success("Updated"); }
    catch (e) { toast.error("Update failed"); }
  };

  const act = async (action, extra = {}) => {
    try { const { data } = await api.post(`/leads/${id}/activity`, { action, note, ...extra }); setLead(data); setNote(""); setFollowUp(""); toast.success(`${action} logged`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not log action"); }
  };

  if (!lead) return <AdminShell title="Lead"><p className="text-emerald-100/50">Loading…</p></AdminShell>;

  const pd = lead.property_details || {};

  return (
    <AdminShell>
      <button onClick={() => navigate("/crm/leads")} data-testid="lead-back" className="flex items-center gap-1.5 text-sm text-emerald-100/70 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Back to leads</button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* main */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-serif text-white" data-testid="lead-name">{lead.full_name}</h1>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${priorityBadge(lead.priority)}`}>{lead.priority}</span>
                </div>
                <div className="mt-1 text-sm text-emerald-100/60">{lead.lead_type} · {lead.source}</div>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full ${statusBadge(lead.status)}`}>{lead.status}</span>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-emerald-100/80 hover:text-white"><Phone className="w-4 h-4 text-brass" />{lead.phone}</a>
              <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-emerald-100/80 hover:text-white"><Mail className="w-4 h-4 text-brass" />{lead.email}</a>
              <div className="flex items-center gap-2 text-emerald-100/80"><MapPin className="w-4 h-4 text-brass" />{[lead.address, lead.postcode].filter(Boolean).join(", ") || "—"}</div>
              <div className="flex items-center gap-2 text-emerald-100/80"><Clock className="w-4 h-4 text-brass" />{new Date(lead.created_at).toLocaleString("en-GB")}</div>
            </div>
          </div>

          {/* property details */}
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-6" data-testid="lead-submitted-details">
            <h3 className="text-sm font-semibold text-white mb-3">Submitted details</h3>
            {(() => {
              const rows = Object.entries(pd).filter(([, v]) => v && v !== "0");
              const empty = !lead.expected_price && rows.length === 0 && !lead.notes;
              if (empty) return <p className="text-sm text-emerald-100/50">No additional details were provided with this enquiry.</p>;
              return (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {lead.expected_price && (<div className="flex justify-between border-b border-white/5 py-1.5"><dt className="text-emerald-100/70">Expected price</dt><dd className="text-white">{lead.expected_price}</dd></div>)}
                  {rows.map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-white/5 py-1.5 gap-4"><dt className="text-emerald-100/70 capitalize">{k.replace(/_/g, " ")}</dt><dd className="text-white text-right break-words">{String(v)}</dd></div>
                  ))}
                </dl>
              );
            })()}
            {lead.notes && (<div className="mt-4"><div className="text-emerald-100/50 text-xs mb-1">Notes / message</div><p className="text-sm text-emerald-100/90 whitespace-pre-wrap">{lead.notes}</p></div>)}
          </div>

          {/* photos */}
          {lead.attachments?.length > 0 && (
            <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-6" data-testid="lead-photos">
              <h3 className="text-sm font-semibold text-white mb-3">Uploaded photographs ({lead.attachments.length})</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {lead.attachments.map((fid) => (
                  <a key={fid} href={images[fid]} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden bg-black/20 aspect-square">
                    {images[fid] ? <img src={images[fid]} alt="upload" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-xs text-emerald-100/40">Loading…</div>}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* activity history */}
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-6" data-testid="lead-activity">
            <h3 className="text-sm font-semibold text-white mb-3">Activity history</h3>
            <ol className="space-y-3">
              {(lead.activity_history || []).map((a) => (
                <li key={a.id} className="flex gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-brass mt-1.5 shrink-0" />
                  <div><div className="text-white">{a.action} {a.note && <span className="text-emerald-100/60">— {a.note}</span>}</div><div className="text-[11px] text-emerald-100/40">{new Date(a.created_at).toLocaleString("en-GB")} · {a.actor}</div></div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* sidebar actions */}
        <div className="space-y-6">
          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-6 space-y-4">
            <h3 className="text-sm font-semibold text-white">Manage</h3>
            <div><label className="text-xs text-emerald-100/50">Status</label>
              <select value={lead.status} onChange={(e) => patch({ status: e.target.value })} data-testid="lead-status-select" className="mt-1 w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
            </div>
            <div><label className="text-xs text-emerald-100/50">Priority</label>
              <select value={lead.priority} onChange={(e) => patch({ priority: e.target.value })} data-testid="lead-priority-select" className="mt-1 w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">{PRIORITIES.map((s) => <option key={s}>{s}</option>)}</select>
            </div>
            <div><label className="text-xs text-emerald-100/50">Assigned to</label>
              <input defaultValue={lead.assigned_to} onBlur={(e) => patch({ assigned_to: e.target.value })} data-testid="lead-assigned" className="mt-1 w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" placeholder="Name" />
            </div>
            <div><label className="text-xs text-emerald-100/50">Next follow-up</label>
              <input type="datetime-local" defaultValue={lead.next_follow_up_date ? isoLocal(lead.next_follow_up_date) : ""} onChange={(e) => patch({ next_follow_up_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="lead-followup-date" className="mt-1 w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
            </div>
          </div>

          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-6 space-y-3">
            <h3 className="text-sm font-semibold text-white">Log an action</h3>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="action-note" rows={3} placeholder="Add a note…" className="w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-emerald-100/40" />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => act("Called")} data-testid="action-called" className="flex items-center justify-center gap-1.5 text-sm bg-white/5 hover:bg-white/10 text-white rounded-lg py-2"><PhoneCall className="w-4 h-4 text-emerald-300" /> Called</button>
              <button onClick={() => act("Emailed")} data-testid="action-emailed" className="flex items-center justify-center gap-1.5 text-sm bg-white/5 hover:bg-white/10 text-white rounded-lg py-2"><Send className="w-4 h-4 text-blue-300" /> Emailed</button>
              <button onClick={() => act("Viewed")} data-testid="action-viewed" className="flex items-center justify-center gap-1.5 text-sm bg-white/5 hover:bg-white/10 text-white rounded-lg py-2"><Eye className="w-4 h-4 text-purple-300" /> Viewed</button>
              <button onClick={() => act("Offer made")} data-testid="action-offer" className="flex items-center justify-center gap-1.5 text-sm bg-white/5 hover:bg-white/10 text-white rounded-lg py-2"><Handshake className="w-4 h-4 text-brass" /> Offer made</button>
            </div>
            <div className="pt-2 border-t border-white/10">
              <label className="text-xs text-emerald-100/50">Follow-up required — set a date</label>
              <input type="datetime-local" value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="followup-required-date" className="mt-1 w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
              <button onClick={() => { if (!followUp) return toast.error("Please choose a follow-up date"); act("Follow-up required", { next_follow_up_date: new Date(followUp).toISOString() }); }} data-testid="action-followup" className="mt-2 w-full flex items-center justify-center gap-1.5 text-sm bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-lg py-2"><CalendarPlus className="w-4 h-4" /> Set follow-up</button>
            </div>
          </div>

          <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-6 text-xs text-emerald-100/50 space-y-1">
            <div className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Lead ID: {lead.lead_id}</div>
            <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Source: {lead.source}</div>
            {(lead.utm_source || lead.utm_campaign) && <div>UTM: {lead.utm_source} / {lead.utm_medium} / {lead.utm_campaign}</div>}
            {lead.campaign && <div>Campaign: {lead.campaign}</div>}
            {lead.last_contact_date && <div>Last contact: {new Date(lead.last_contact_date).toLocaleString("en-GB")}</div>}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
